/**
 * One candidate's final-round card.
 *
 * Split out of FinalRoundsTab.tsx when the Overdue tab's chip routing pushed that file over the
 * repo's 500-line ceiling. Moved VERBATIM apart from the imports. It was already exported for the
 * Requests tab's scheduling connection, so its own file is where it belonged anyway.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import { CandidateChips } from './CandidateChips';
import { cn } from '../../../../shared';
import type { SchedulingCandidate, TrackingMap } from '../../types/taActionBoard';
import { HandledChip, InterviewTrail, ProposedTimesTable } from './CandidateCard';
import { CancellationTag } from './CancellationTag';
import { PhoneChip } from './PhoneChip';
import { StatusBadge, TrackingTable } from './TrackingTable';
// Type-only, so this is erased at compile time and creates no runtime import cycle with the tab
// that still owns the action union.
import type { FinalRoundAction } from './FinalRoundsTab';

/** Exported for the Requests tab's scheduling connection — same card, same actions. */
export function FinalRoundCard({
  candidate,
  tracking,
  highlighted = false,
  handledLabel,
  hasChains,
  onAction,
}: {
  candidate: SchedulingCandidate;
  tracking: TrackingMap;
  /** Arrived at from the Overdue tab — ringed, like a focused feedback card. */
  highlighted?: boolean;
  handledLabel: string | null;
  hasChains: boolean;
  onAction: (action: FinalRoundAction) => void;
}) {
  const doc = tracking[candidate.opportunityId];
  const status = doc?.status || 'not started';
  return (
    <div
      // Landing target for the Overdue tab's stuck chips (useFocusCard scrolls to this id).
      id={`ta-card-${candidate.opportunityId}`}
      tabIndex={-1}
      className={cn(
        'rounded-xl bg-white shadow-sm ring-1 transition-all',
        highlighted ? 'ring-2 ring-blue-400' : 'ring-gray-200',
        handledLabel && 'opacity-45'
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold text-gray-900">{candidate.name}</span>
          <a
            href={candidate.leverUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-xs text-blue-600 hover:underline"
          >
            ↗ Lever
          </a>
          <span className="shrink-0 text-xs text-gray-400">
            {candidate.stageName} · waiting {candidate.waitingDays.toFixed(1)}d
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <CandidateChips candidateName={candidate.name} opportunityId={candidate.opportunityId} />
          {handledLabel && <HandledChip label={handledLabel} />}
          <PhoneChip phone={candidate.phone} />
          <StatusBadge status={status} />
        </div>
      </div>

      {/* WHY they are back here. ABOVE the proposed times on purpose: those are
          the times that were proposed and then cancelled, and reading them
          first without the cancellation is how a recruiter re-proposes them. */}
      {candidate.cancellation && (
        <div className="px-4 pt-3">
          <CancellationTag cancellation={candidate.cancellation} />
        </div>
      )}

      <ProposedTimesTable tracking={doc} kind="fr-proposed" />
      <div className="grid gap-4 px-4 py-3 lg:grid-cols-2">
        <InterviewTrail candidate={candidate} />
        <TrackingTable tracking={doc} scope="final" />
      </div>

      <div className="flex flex-wrap gap-2 border-t border-gray-100 px-4 py-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAction({ type: 'fr-propose', candidate })}
          title={hasChains ? undefined : 'No worked-out times — a custom one can still be proposed'}
        >
          Propose times
        </Button>
        <Button size="sm" onClick={() => onAction({ type: 'fr-book', candidate })}>
          Book final round
        </Button>
        {/* Taking back what was already promised. Offered on a PROPOSED or
            CONFIRMED round: once it is booked the candidate holds a calendar
            invite, and un-inviting them is a cancellation, not a withdrawal. */}
        {(status === 'final round proposed' || status === 'final round confirmed') && (
          <Button size="sm" variant="outline" onClick={() => onAction({ type: 'fr-withdraw', candidate })}>
            Withdraw proposed times
          </Button>
        )}
        {status === 'final round booked' && (
          <Button size="sm" variant="outline" onClick={() => onAction({ type: 'fr-debrief', candidate })}>
            Debrief done — decide
          </Button>
        )}
      </div>
    </div>
  );
}
